import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle2, FileText, Eye, Download, Mail } from 'lucide-react';
import { DocumentUploadButton } from '@/components/documents/DocumentUploadButton';
import { DocumentPreviewModal } from '@/components/documents/DocumentPreviewModal';
import { toast as sonnerToast } from 'sonner';
import { FinalizeWithSignatures } from '@/components/documents/FinalizeWithSignatures';
import { embedSignaturesIntoHtml } from '@/lib/signatureEmbedding';
import { DocumentSigningControl } from '../DocumentSigningControl';
import { SealApplyCheckbox } from '../SealApplyCheckbox';
import { useAssociationSeal } from '../useAssociationSeal';
import { generatedFilePath } from '../docProvenance';
import { finishLegacyHtml } from '../legacyDocumentFinish';
import { legacySigningMeta } from '../legacySigningSpecs';
import { downloadDocumentById } from '@/lib/downloadDocumentById';

interface MemberPhase2Props {
  task: any;
  phaseData: any;
  onPhaseComplete: () => void;
}

export default function MemberPhase2({ task, phaseData, onPhaseComplete }: MemberPhase2Props) {
  const { toast } = useToast();
  const [documents, setDocuments] = useState<any[]>([]);
  const seal = useAssociationSeal(task.una_id);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState<string | null>(null);
  const [previewDocument, setPreviewDocument] = useState<any>(null);
  const [emailWizardOpen, setEmailWizardOpen] = useState(false);
  const [selectedDocForEmail, setSelectedDocForEmail] = useState<string | null>(null);
  const [signatureWizardOpen, setSignatureWizardOpen] = useState(false);
  const [selectedDocForSignature, setSelectedDocForSignature] = useState<any>(null);
  const [unaData, setUnaData] = useState<any>(null);
  const [requestingMemberSignature, setRequestingMemberSignature] = useState(false);
  const [documentStatuses, setDocumentStatuses] = useState<Record<string, { status: string; label: string; color: string }>>({});

  const workflowData = task.workflow_data || {};

  const requiredDocuments = [
    { 
      key: 'letter_member_acceptance', 
      title: 'Member Acceptance Letter',
      requiresSignature: true
    },
    { 
      key: 'membership_attestation', 
      title: 'Membership Attestation',
      requiresSignature: true
    },
    { 
      key: 'cover_member_records_filing', 
      title: 'Member Records Filing Cover Sheet',
      requiresSignature: true
    },
  ];

  useEffect(() => {
    loadDocuments();
    loadUnaData();
  }, [task.id]);

  useEffect(() => {
    updateDocumentStatuses();
  }, [documents]);

  async function loadUnaData() {
    if (!task.una_id) return;
    
    try {
      const { data: una, error: unaError } = await supabase
        .from('unas')
        .select('*, una_members!una_members_una_id_fkey(*)')
        .eq('id', task.una_id)
        .single();

      if (unaError) throw unaError;
      setUnaData(una);
    } catch (error: any) {
      console.error('Error loading UNA data:', error);
    }
  }

  async function loadDocuments() {
    try {
      setLoading(true);
      
      const { data: phase2Docs, error } = await supabase
        .from('generated_documents')
        .select('*')
        .eq('task_id', task.id)
        .contains('merge_field_data', { phase: 'phase_2' })
        .order('generated_at', { ascending: true });

      if (error) throw error;
      setDocuments(phase2Docs || []);
    } catch (error: any) {
      console.error('Error loading documents:', error);
      toast({
        title: 'Error Loading Documents',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }

  async function updateDocumentStatuses() {
    const statuses: Record<string, { status: string; label: string; color: string }> = {};
    
    for (const docReq of requiredDocuments) {
      const doc = documents.find(d => d.merge_field_data?.template_key === docReq.key);
      
      if (!doc) {
        statuses[docReq.key] = { status: 'not_generated', label: 'Not Generated', color: 'secondary' };
        continue;
      }

      // Check for member signature requests specifically for Acceptance Letter
      if (docReq.key === 'letter_member_acceptance' && docReq.requiresSignature) {
        const { data: sigRequests } = await supabase
          .from('signature_requests')
          .select('status')
          .eq('document_id', doc.id)
          .eq('role_signing_as', 'Member');

        const pendingMemberSig = sigRequests?.find(sr => sr.status === 'pending');
        const completedMemberSig = sigRequests?.find(sr => sr.status === 'completed');

        if (pendingMemberSig) {
          statuses[docReq.key] = { status: 'pending_member', label: 'Pending Member Signature', color: 'secondary' };
          continue;
        }

        if (completedMemberSig && doc.status === 'draft') {
          statuses[docReq.key] = { status: 'member_signed', label: 'Member Signed - Awaiting TS', color: 'default' };
          continue;
        }
      }

      if (docReq.requiresSignature) {
        if (doc.status === 'draft') {
          statuses[docReq.key] = { status: 'draft', label: 'Draft - Awaiting Signature', color: 'secondary' };
          continue;
        }
        if (doc.status === 'finalized') {
          statuses[docReq.key] = { status: 'finalized', label: 'Signed & Complete', color: 'default' };
          continue;
        }
      }

      statuses[docReq.key] = { status: 'generated', label: 'Generated', color: 'default' };
    }
    
    setDocumentStatuses(statuses);
  }

  async function handleRequestMemberSignature(doc: any) {
    setRequestingMemberSignature(true);
    try {
      // Validate required workflow data
      if (!workflowData.member_email || !workflowData.member_full_name) {
        throw new Error('Member email and name are required from workflow data');
      }

      console.log('Requesting signature from new member:', {
        memberEmail: workflowData.member_email,
        memberName: workflowData.member_full_name,
        documentId: doc.id
      });

      // Use the new edge function for prospective members (no memberId required)
      const { data, error } = await supabase.functions.invoke('send-new-member-signature-request', {
        body: {
          documentId: doc.id,
          unaId: task.una_id,
          memberEmail: workflowData.member_email,
          memberName: workflowData.member_full_name,
          roleSigningAs: 'Member',
          capacity: 'Individual accepting membership'
        }
      });
      
      if (error) throw error;
      
      console.log('✅ Signature request sent successfully:', data);
      
      toast({
        title: 'Success',
        description: `Signature request sent to ${workflowData.member_full_name} (${workflowData.member_email})`,
      });
      
      await loadDocuments();
    } catch (error: any) {
      console.error('Error requesting member signature:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to send signature request',
        variant: 'destructive',
      });
    } finally {
      setRequestingMemberSignature(false);
    }
  }

  async function handleGenerateDocument(docReq: any) {
    try {
      setGenerating(docReq.key);

      const { data: unaData } = await supabase
        .from('unas')
        .select('*')
        .eq('id', task.una_id)
        .single();

      if (!unaData) throw new Error('UNA data not found');

      const html = finishLegacyHtml(
        generateMemberPhase2DocumentHtml(docReq.key, workflowData, unaData),
        { entityName: unaData?.name, sealEnabled: seal.sealEnabled, sealSvg: seal.sealSvg },
      );

      const { data: pdfData, error: pdfError } = await supabase.functions.invoke('generate-pdf-server', {
        body: { html, author: unaData?.name || undefined },
      });

      if (pdfError) throw pdfError;
      if (!pdfData?.pdf) throw new Error('No PDF data returned');

      const pdfBlob = new Blob(
        [Uint8Array.from(atob(pdfData.pdf), c => c.charCodeAt(0))],
        { type: 'application/pdf' }
      );

      const filePath = generatedFilePath(unaData?.name, docReq.key, task.id);

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, pdfBlob, {
          contentType: 'application/pdf',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // Buckets are private — store path as reference
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const createdBy = task.user_id || authSession?.user?.id || null;

      const { data: doc, error: insertError } = await supabase
        .from('generated_documents')
        .insert({
          una_id: task.una_id,
          task_id: task.id,
          document_name: docReq.title,
          document_type: 'workflow',
          file_url: filePath,
          file_path: filePath,
          file_size: pdfBlob.size,
          status: docReq.requiresSignature ? 'draft' : 'final',
          custom_content: html,
          created_by: createdBy,
          merge_field_data: { 
            phase: 'phase_2',
            template_key: docReq.key,
            ...legacySigningMeta(docReq.key, seal.sealEnabled),
            workflow_data: workflowData 
          },
        })
        .select()
        .single();

      if (insertError) throw insertError;

      sonnerToast.success('Document Generated', {
        description: `${docReq.title} created successfully`,
      });

      await loadDocuments();
    } catch (error: any) {
      console.error('Error generating document:', error);
      toast({
        title: 'Generation Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setGenerating(null);
    }
  }

  function handleCollectSignature(doc: any) {
    setSelectedDocForSignature(doc);
    setSignatureWizardOpen(true);
  }

  async function handleMarkPhaseComplete() {
    const missingDocs = requiredDocuments.filter(
      req => !documents.some(d => d.merge_field_data?.template_key === req.key)
    );

    if (missingDocs.length > 0) {
      toast({
        title: 'Documents Required',
        description: 'Please generate all required documents before completing this phase.',
        variant: 'destructive',
      });
      return;
    }

    const unsignedDocs = requiredDocuments.filter(req => {
      const doc = documents.find(d => d.merge_field_data?.template_key === req.key);
      return req.requiresSignature && doc?.status !== 'finalized';
    });

    if (unsignedDocs.length > 0) {
      toast({
        title: 'Signatures Required',
        description: 'Please sign all required documents before completing this phase.',
        variant: 'destructive',
      });
      return;
    }

    toast({
      title: 'Phase 2 Complete',
      description: 'Member notification documents generated and signed. Proceeding to final phase.',
    });

    onPhaseComplete();
  }

  const allComplete = requiredDocuments.every(req => {
    const doc = documents.find(d => d.merge_field_data?.template_key === req.key);
    if (!doc) return false;
    if (req.requiresSignature) return doc.status === 'finalized';
    return true;
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Phase 2: Generate Notification Documents</CardTitle>
          <CardDescription>
            Create acceptance letters and filing documents for the new member
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-medium flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Steps to Complete Phase 2:
            </h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-6">
              <li>Generate Member Acceptance Letter (for member to sign)</li>
              <li>Generate Member Records Filing Cover Sheet</li>
              <li>Distribute documents to member and relevant parties</li>
              <li>Mark phase as complete</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Required Documents</CardTitle>
          <CardDescription>Generate all Phase 2 notification documents</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <SealApplyCheckbox
              available={seal.sealAvailable}
              checked={seal.applySeal}
              onChange={seal.setApplySeal}
            />
            {requiredDocuments.map((docReq) => {
              const statusInfo = documentStatuses[docReq.key] || { status: 'not_generated', label: 'Not Generated', color: 'secondary' };
              const { status, label, color } = statusInfo;
              const doc = documents.find(d => d.merge_field_data?.template_key === docReq.key);
              const isGenerating = generating === docReq.key;
              const isAcceptanceLetter = docReq.key === 'letter_member_acceptance';

              return (
                <div key={docReq.key} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <div className="font-medium">{docReq.title}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={color as any}>{label}</Badge>
                    
                    {!doc ? (
                      <Button
                        onClick={() => handleGenerateDocument(docReq)}
                        disabled={isGenerating}
                        size="sm"
                      >
                        {isGenerating ? 'Generating...' : 'Generate'}
                      </Button>
                    ) : (
                      <>
                        {/* Acceptance Letter: Show "Request Member Signature" button */}
                        {isAcceptanceLetter && doc.status === 'draft' && status === 'draft' && (
                          <Button 
                            onClick={() => handleRequestMemberSignature(doc)}
                            disabled={requestingMemberSignature}
                            size="sm"
                          >
                            {requestingMemberSignature ? 'Sending...' : 'Request Member Signature'}
                          </Button>
                        )}

                        {/* Show Sign & Finalize for all draft docs or when member has signed */}
                        {doc.status === 'draft' && docReq.requiresSignature && status !== 'pending_member' && (
                          // For Acceptance Letter: Only show TS button after member has signed
                          (!isAcceptanceLetter || status === 'member_signed') && (
                            <Button onClick={() => handleCollectSignature(doc)} size="sm">
                              Sign & Finalize (TS)
                            </Button>
                          )
                        )}
                        
                        {doc && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setPreviewDocument(doc)}
                            >
                              <Eye className="h-3 w-3" />
                              Preview
                            </Button>
                          
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                try {
                                  await downloadDocumentById(doc.id, { underscoreFilename: true });
                                } catch (error) {
                                  console.error('Download error:', error);
                                }
                              }}
                            >
                              <Download className="h-3 w-3" />
                              Download
                            </Button>

                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedDocForEmail(doc.id);
                                setEmailWizardOpen(true);
                              }}
                            >
                              <Mail className="h-3 w-3" />
                              Email
                            </Button>
                            
                            <DocumentUploadButton
                              documentId={doc.id}
                              documentName={doc.document_name}
                              onUploadComplete={() => loadDocuments()}
                              fileUrl={doc.file_url}
                              hasUploadedFile={doc.file_path?.includes('-uploaded.pdf')}
                            />
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {allComplete && (
            <div className="mt-6 p-4 bg-green-50 dark:bg-green-950 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-green-900 dark:text-green-100">Phase 2 Requirements Met</p>
                  <p className="text-sm text-green-700 dark:text-green-300">All notification documents have been generated</p>
                </div>
                <Button onClick={handleMarkPhaseComplete}>
                  Complete Phase 2
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {previewDocument && (
        <DocumentPreviewModal
          document={previewDocument}
          open={!!previewDocument}
          onClose={() => setPreviewDocument(null)}
        />
      )}

      {signatureWizardOpen && selectedDocForSignature && (
        <FinalizeWithSignatures
          documentTitle={`Finalize ${selectedDocForSignature?.document_name || 'Document'}`}
          treasurySecretaryName={unaData?.treasury_secretary_name || 'Secretary Treasurer'}
          primaryOfficerRoleLabel={unaData?.primary_officer_role || 'Secretary Treasurer'}
          requiredSigners={['treasury_secretary']}
          onFinalize={async (signatures) => {
            try {
              // Fetch FRESH custom_content from database to preserve any signatures already embedded
              const { data: freshDocData, error: fetchError } = await supabase
                .from('generated_documents')
                .select('custom_content')
                .eq('id', selectedDocForSignature.id)
                .single();
              
              if (fetchError) throw fetchError;
              if (!freshDocData?.custom_content) {
                throw new Error('Document HTML not found');
              }
              
              const signatureDataArray = [{
                role: 'treasury_secretary',
                signer_id: 'treasury_secretary',
                signature_data: signatures.treasury_secretary_signature,
                signed_at: signatures.treasury_secretary_signature_date,
              }];

              const updatedHtml = await embedSignaturesIntoHtml(
                freshDocData.custom_content,
                signatureDataArray,
                new Date().toISOString()
              );

              const { data: pdfData, error: pdfError } = await supabase.functions.invoke('generate-pdf-server', {
                body: { html: updatedHtml, author: unaData?.name || undefined },
              });

              if (pdfError) throw pdfError;
              if (!pdfData?.pdf) throw new Error('No PDF data returned');

              const pdfBlob = new Blob(
                [Uint8Array.from(atob(pdfData.pdf), c => c.charCodeAt(0))],
                { type: 'application/pdf' }
              );

              const filePath = generatedFilePath(unaData?.name, `${selectedDocForSignature.merge_field_data?.template_key}_signed`, selectedDocForSignature.id);

              const { error: uploadError } = await supabase.storage
                .from('documents')
                .upload(filePath, pdfBlob, {
                  contentType: 'application/pdf',
                  upsert: true,
                });

              if (uploadError) throw uploadError;

              // Buckets are private — store path as reference
              const { error: updateError } = await supabase
                .from('generated_documents')
                .update({
                  status: 'finalized',
                  finalized_at: new Date().toISOString(),
                  finalized_by: task.user_id,
                  file_url: filePath,
                  file_path: filePath,
                  file_size: pdfBlob.size,
                  signed_file_url: filePath,
                  custom_content: updatedHtml,
                  signature_history: {
                    finalized_signatures: {
                      treasury_secretary_signature: signatures.treasury_secretary_signature,
                      treasury_secretary_date: signatures.treasury_secretary_signature_date,
                      finalized_at: new Date().toISOString(),
                    }
                  }
                })
                .eq('id', selectedDocForSignature.id);

              if (updateError) throw updateError;

              sonnerToast.success('Document Finalized', {
                description: 'Secretary Treasurer signature applied successfully',
              });

              setSignatureWizardOpen(false);
              setSelectedDocForSignature(null);
              await loadDocuments();
            } catch (error: any) {
              console.error('Error finalizing document:', error);
              toast({
                title: 'Finalization Failed',
                description: error.message,
                variant: 'destructive',
              });
            }
          }}
          onCancel={() => {
            setSignatureWizardOpen(false);
            setSelectedDocForSignature(null);
          }}
        />
      )}
    </div>
  );
}

// Helper function to generate Phase 2 member documents with professional styling
function generateMemberPhase2DocumentHtml(docKey: string, workflowData: any, unaData: any): string {
  const professionalStyles = `
    <style>
      @page { margin: 1in; }
      body { 
        font-family: Cambria, 'Times New Roman', serif; 
        font-size: 11pt;
        line-height: 1.5;
        color: #000;
        margin: 0;
        padding: 0;
      }
      .letterhead { 
        text-align: center; 
        font-weight: bold;
        font-size: 14pt;
        margin-bottom: 0.5in;
        letter-spacing: 1px;
      }
      .date { 
        margin-bottom: 0.3in; 
        font-style: italic;
      }
      .address-block {
        margin-bottom: 0.3in;
        line-height: 1.3;
      }
      .re-line {
        font-weight: bold;
        margin: 0.2in 0;
      }
      .signature-block {
        margin-top: 0.6in;
        page-break-inside: avoid;
      }
      .signature-line {
        display: inline-block;
        width: 3in;
        border-bottom: 1px solid #000;
        margin-bottom: 0.05in;
      }
      .section-title {
        font-weight: bold;
        margin-top: 0.25in;
        margin-bottom: 0.1in;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        margin: 0.2in 0;
      }
      td {
        padding: 0.1in 0.15in;
        vertical-align: top;
      }
      ul {
        margin: 0.1in 0;
        padding-left: 0.3in;
      }
      li {
        margin-bottom: 0.05in;
      }
    </style>
  `;

  // Helper function to format dates consistently
  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const memberName = workflowData.member_full_name || '[Member Name]';
  const memberRole = workflowData.member_role || '[Member Role]';
  const appointmentDate = formatDate(workflowData.appointment_date);
  const unaName = unaData?.name || '[UNA Name]';

  if (docKey === 'letter_member_acceptance') {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        ${professionalStyles}
      </head>
      <body>
        <div class="letterhead">MEMBER ACCEPTANCE LETTER</div>
        
        <div class="date">${today}</div>
        
        <p><strong>To:</strong> Board of ${unaName}</p>
        <p><strong>From:</strong> ${memberName}, ${memberRole}-Designate</p>
        <p><strong>Re:</strong> Acceptance of Member Appointment</p>
        
        <hr style="margin: 0.3in 0; border: none; border-top: 1px solid #000;">
        
        <p>I, ${memberName}, hereby formally accept the appointment to serve as <strong>${memberRole}</strong> of ${unaName}, effective ${appointmentDate}.</p>
        
        <div class="section-title">Acknowledgment of Documents Received:</div>
        <p>I acknowledge receipt and thorough review of the following documents:</p>
        <ul>
          <li>Member Appointment Resolution dated ${today}</li>
          <li>Member Information Collection Form</li>
          <li>${unaName} Operating Agreement</li>
        </ul>
        
        <div class="section-title">Responsibilities Acknowledged:</div>
        <p>I understand and acknowledge that as ${memberRole}, I will be responsible for:</p>
        <ul>
          <li>Fulfilling all duties specified in the Operating Agreement and applicable law</li>
          <li>Acting in good faith and in the best interests of ${unaName}</li>
          <li>Maintaining confidentiality of sensitive organizational information</li>
          <li>Complying with all applicable federal, state, and local laws</li>
          <li>Participating actively in meetings and organizational initiatives</li>
        </ul>
        
        <div class="section-title">Commitment Statement:</div>
        <p>I commit to executing the duties of ${memberRole} with diligence, integrity, and professionalism. I understand that this position requires dedication and that I am expected to act in accordance with the highest ethical standards.</p>
        
        <div class="signature-block">
          <table style="margin-top: 0.6in;">
            <tr>
              <td style="width: 50%;">
                <strong>Member Signature:</strong><br>
                <div data-signer-id="member" data-signature-role="Member" style="width: 3in; min-height: 0.8in; border-bottom: 1px solid #000; display: flex; align-items: center; justify-content: center; margin-bottom: 0.1in;">
                  <em style="color: #666; font-size: 9pt;">[Member Signature]</em>
                </div>
                <span style="font-size: 9pt;">${memberName}, ${memberRole}-Designate</span>
              </td>
              <td style="width: 50%;">
                <strong>Date:</strong><br>
                <span style="font-size: 9pt;">${today}</span>
              </td>
            </tr>
          </table>
          
          <p style="margin-top: 0.3in;">
            <strong>Print Name:</strong> ${memberName}
          </p>
          
          <hr style="margin: 0.5in 0; border: none; border-top: 1px solid #ccc;">
          
          <p><strong>Acknowledged and Accepted by ${unaName}:</strong></p>
          <table style="margin-top: 0.3in;">
            <tr>
              <td style="width: 60%;">
                <strong>${unaData?.primary_officer_role || 'Secretary Treasurer'} Signature:</strong><br>
                <div data-signer-id="primary_officer" data-signature-role="${unaData?.primary_officer_role || 'Secretary Treasurer'}" style="width: 3in; min-height: 0.8in; border-bottom: 1px solid #000; display: flex; align-items: center; justify-content: center; margin-bottom: 0.1in;">
                  <em style="color: #666; font-size: 9pt;">[${unaData?.primary_officer_role || 'Secretary Treasurer'} Signature]</em>
                </div>
                <span style="font-size: 9pt;">${unaData?.treasury_secretary_name || unaData?.primary_officer_role || 'Secretary Treasurer'}</span>
              </td>
              <td style="width: 40%;">
                <strong>Date:</strong><br>
                <span style="font-size: 9pt;">${today}</span>
              </td>
            </tr>
          </table>
        </div>
      </body>
      </html>
    `;
  }

  if (docKey === 'cover_member_records_filing') {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        ${professionalStyles}
      </head>
      <body>
        <div class="letterhead">MEMBER RECORDS FILING COVER SHEET</div>
        
        <div class="date">${today}</div>
        
        <p><strong>UNA Name:</strong> ${unaName}</p>
        <p><strong>Filing Date:</strong> ${today}</p>
        <p><strong>Filing Type:</strong> New Member Appointment</p>
        
        <hr style="margin: 0.3in 0; border: none; border-top: 2px solid #000;">
        
        <div class="section-title">Member Information:</div>
        <table style="border: 1px solid #000;">
          <tr>
            <td style="width: 40%; font-weight: bold; border-right: 1px solid #000;">Full Legal Name:</td>
            <td style="width: 60%;">${memberName}</td>
          </tr>
          <tr>
            <td style="width: 40%; font-weight: bold; border-right: 1px solid #000; border-top: 1px solid #000;">Member Role:</td>
            <td style="width: 60%; border-top: 1px solid #000;">${memberRole}</td>
          </tr>
          <tr>
            <td style="width: 40%; font-weight: bold; border-right: 1px solid #000; border-top: 1px solid #000;">Appointment Date:</td>
            <td style="width: 60%; border-top: 1px solid #000;">${appointmentDate}</td>
          </tr>
          <tr>
            <td style="width: 40%; font-weight: bold; border-right: 1px solid #000; border-top: 1px solid #000;">Email Address:</td>
            <td style="width: 60%; border-top: 1px solid #000;">${workflowData.member_email || 'N/A'}</td>
          </tr>
        </table>
        
        <div class="section-title">Documents Included in This Filing:</div>
        <ul>
          <li>☑ Member Appointment Resolution (Board Approved)</li>
          <li>☑ Member Information Collection Form</li>
          <li>☑ Member Acceptance Letter (Signed by Member)</li>
          <li>☑ Updated Member Roster</li>
        </ul>
        
        <div class="section-title">Filing Certification:</div>
        <p>I hereby certify that the documents included in this filing are true and accurate copies of the original documents approved and executed by ${unaName}.</p>
        
        <div class="signature-block">
          <p><strong>Certified by:</strong></p>
          <table style="margin-top: 0.3in;">
            <tr>
              <td style="width: 60%;">
                <strong>${unaData?.primary_officer_role || 'Secretary Treasurer'} Signature:</strong><br>
                <div data-signer-id="primary_officer" data-signature-role="${unaData?.primary_officer_role || 'Secretary Treasurer'}" style="width: 3in; min-height: 0.8in; border-bottom: 1px solid #000; display: flex; align-items: center; justify-content: center; margin-bottom: 0.1in;">
                  <em style="color: #666; font-size: 9pt;">[${unaData?.primary_officer_role || 'Secretary Treasurer'} Signature]</em>
                </div>
                <span style="font-size: 9pt;">${unaData?.treasury_secretary_name || unaData?.primary_officer_role || 'Secretary Treasurer'}</span>
              </td>
              <td style="width: 40%;">
                <strong>Date:</strong><br>
                <span style="font-size: 9pt;">${today}</span>
              </td>
            </tr>
          </table>
        </div>
        
        <p style="margin-top: 0.5in; font-style: italic; font-size: 9pt; border-top: 1px solid #ccc; padding-top: 0.2in;">
          <strong>Filing Instructions:</strong> This cover sheet should be filed with all member appointment documents in the official UNA governance records.
        </p>
      </body>
      </html>
    `;
  }

  if (docKey === 'membership_attestation') {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Membership Attestation</title>
        ${professionalStyles}
      </head>
      <body>
        <div class="letterhead">MEMBERSHIP ATTESTATION</div>

        <div class="date">${today}</div>

        <p>
          ${unaName} attests that ${memberName} was admitted as ${memberRole} on ${appointmentDate},
          and holds that place today.
        </p>

        <p>
          The Letter of Invitation and the Letter of Acceptance for this membership are kept
          together in the Record of the Association.
        </p>

        <div class="signature-block">
          <p><strong>Attested by:</strong></p>
          <div data-signer-id="primary_officer" data-signature-role="${unaData?.primary_officer_role || 'Secretary Treasurer'}" style="width: 3in; min-height: 0.8in; border-bottom: 1px solid #000; margin-bottom: 0.1in;"></div>
          <span style="font-size: 9pt;">${unaData?.treasury_secretary_name || unaData?.primary_officer_role || 'Secretary Treasurer'}, ${unaData?.primary_officer_role || 'Secretary Treasurer'}</span>
          <p style="margin-top: 0.2in;"><strong>Date:</strong> ${today}</p>
        </div>
      </body>
      </html>
    `;
  }

  return '';
}
